import * as React from 'react'
import { ReactNode } from 'react'

import { ArrowUpDownIcon, ListFilterIcon, ShapesIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import { CaretUpIcon } from '~/client/src/shared/components/icons/CaretUpIcon'
import { ChevronDownIcon } from '~/client/src/shared/components/icons/ChevronDownIcon'

type FacetButtonType = 'group' | 'sort' | 'filter'
type FacetButtonSize = 'sm' | 'lg'

interface IProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'onClick'
> {
  type?: FacetButtonType
  icon?: ReactNode
  label?: string
  className?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  isSelected?: boolean
  isOpen?: boolean
  isActive?: boolean
  isDisabled?: boolean
  isIconOnly?: boolean
  showChevron?: boolean
  badgeCount?: number
  size?: FacetButtonSize
}

const BASE =
  'tw:inline-flex tw:items-center tw:justify-center tw:h-8 tw:rounded tw:border tw:border-solid tw:border-border tw:text-body-md tw:cursor-pointer tw:select-none tw:outline-none'

const TYPE_ICONS: Record<FacetButtonType, React.FC<{ className?: string }>> = {
  group: ShapesIcon,
  sort: ArrowUpDownIcon,
  filter: ListFilterIcon,
}

// forwardRef + `...rest` merges base-ui render-prop ref/onClick onto button — avoids double tab stop.
/**
 * @deprecated Use GroupButton, SortButton, or FilterButton from
 * `~/client/src/shared/components/Filters/triggers` for new filter surfaces.
 */
const FacetButton = React.forwardRef<HTMLButtonElement, IProps>(
  (
    {
      type,
      icon,
      label,
      className,
      onClick,
      isSelected,
      isOpen,
      isActive = true,
      isDisabled,
      isIconOnly,
      showChevron = true,
      badgeCount,
      size = 'sm',
      ...rest
    },
    ref,
  ) => {
    const hasIcon = !!icon || !!type
    const hasLabel = !isIconOnly && !!label
    const showBadge = typeof badgeCount === 'number' && badgeCount > 0
    const shouldShowChavronForFilter =
      type === 'filter' && isIconOnly && showBadge ? false : true
    const shouldShowChevron =
      showChevron &&
      (!isIconOnly || type !== 'filter' || shouldShowChavronForFilter)
    const chevronUp = isOpen !== undefined ? isOpen : isSelected
    const ChevronIcon = chevronUp ? CaretUpIcon : ChevronDownIcon
    const TypeIcon = type ? TYPE_ICONS[type] : null

    return (
      <button
        ref={ref}
        type="button"
        data-chip-type={type}
        className={classList({
          [BASE]: true,
          'tw:h-10': size === 'lg',
          'tw:px-[10px]': !isIconOnly,
          'tw:px-2': isIconOnly,
          'tw:bg-accent tw:text-foreground': isActive,
          'tw:bg-background tw:text-muted-foreground': !isActive,
          'tw:cursor-not-allowed tw:opacity-60': isDisabled,
          [className]: !!className,
        })}
        onClick={isDisabled ? undefined : onClick}
        disabled={isDisabled}
        {...rest}
      >
        <span className="tw:inline-flex tw:items-center tw:gap-1 tw:max-w-[250px] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
          {hasIcon && (
            <span className="tw:inline-flex tw:items-center">
              {icon || (TypeIcon && <TypeIcon className="tw:size-4" />)}
            </span>
          )}
          {hasLabel && (
            <span className="tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
              {label}
            </span>
          )}
          {showBadge && (
            <span className="tw:inline-flex tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-primary-foreground tw:size-[18px] tw:text-xs tw:leading-none">
              {badgeCount}
            </span>
          )}
          {shouldShowChevron && <ChevronIcon size="xs" />}
        </span>
      </button>
    )
  },
)

FacetButton.displayName = 'FacetButton'

export default FacetButton
